/**
 * SimulationView v2 (spec §6.3 + §11).
 *
 * 4-tab layout: Sequence · Waterfall · Blast Radius · Coverage
 * Autonomy level selector (0–3) controls agent gate evaluation depth.
 * CoverageTracker accumulates hits across all scenario runs.
 * Compare mode still works in the Sequence tab.
 */
import { useEffect, useMemo, useState, useRef } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  simulate,
  type ScenarioAction,
  type SimulationTrace,
  type EnrichedSimulationTrace,
} from '../state/simulator.js';
import { simulateV2 } from '../state/simulatorV2.js';
import { CANONICAL_SCENARIOS_V2 } from '../state/scenarioStore.js';
import { CoverageTracker } from '../state/coverageTracker.js';
import { applyOps, type SandboxOp } from '../state/sandboxOps.js';
import type { Sandbox } from '../state/atlasApiTypes.js';
import { SequenceDiagram } from './SequenceDiagram.js';
import { WaterfallTimeline } from './WaterfallTimeline.js';
import { BlastRadiusV2 } from './BlastRadiusV2.js';
import { CoverageReport } from './CoverageReport.js';
import { TabBar } from '../components/TabBar.js';
import { Icon } from '../components/Icon.js';
import { EntityChip } from '../components/EntityChip.js';
import { SIMULATION_PLAY_MS as PLAY_MS } from '../state/timings.js';
import { entityById } from '../state/selectors.js';

const SIM_TABS = [
  { id: 'sequence' as const, label: 'Sequence', icon: 'account_tree' },
  { id: 'waterfall' as const, label: 'Waterfall', icon: 'waterfall_chart' },
  { id: 'blast' as const, label: 'Blast Radius', icon: 'hub' },
  { id: 'coverage' as const, label: 'Coverage', icon: 'grid_on' },
] as const;

type SimTab = typeof SIM_TABS[number]['id'];

const ACTOR_LABEL: Record<string, string> = {
  user: 'USER',
  cf: 'WRITE',
  'event-bus': 'EVENT',
  'cf-callable': 'CALL',
};

function stepDomainColor(manifest: AtlasManifest, step: SimulationTrace['steps'][number]): string {
  const entityId = step.writes[0];
  if (!entityId) return 'var(--text3)';
  const entity = entityById(manifest, entityId);
  if (!entity) return 'var(--text3)';
  const domain = manifest.domains.find((d) => d.id === entity.domain);
  return domain ? `var(--${domain.color.replace(/^--/, '')})` : 'var(--text3)';
}

function uniqueWrites(t: SimulationTrace | EnrichedSimulationTrace): Set<string> {
  const s = new Set<string>();
  for (const step of t.steps) for (const w of step.writes) s.add(w);
  return s;
}

export interface SimulationViewProps {
  manifest: AtlasManifest;
  focus?: string;
  onNavigate?: (view: 'scenario-builder', seed?: string) => void;
  sandboxes?: Sandbox[];
  /** Pre-select this sandbox as the overlay on mount (from navigateSimulateSandbox). */
  initialSandboxId?: string;
}

export function SimulationView({ manifest, focus, onNavigate, sandboxes = [], initialSandboxId }: SimulationViewProps): React.JSX.Element {
  // Merge canonical v2 scenarios + legacy actions into a unified list.
  const allScenarios: readonly ScenarioAction[] = useMemo(
    () => CANONICAL_SCENARIOS_V2,
    [],
  );

  const initialAction = useMemo(() => {
    if (!focus) return '';
    for (const a of allScenarios) {
      const t = simulate(manifest, a);
      if (t.steps.some((s) => s.writes.includes(focus))) return a.id;
    }
    return '';
  }, [focus, manifest, allScenarios]);

  const [actionId, setActionId] = useState<string>(initialAction);
  const [compareActionId, setCompareActionId] = useState<string>('');
  const [selectedStep, setSelectedStep] = useState<number>(0);
  const [playing, setPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState<SimTab>('sequence');
  const [autonomyLevel, setAutonomyLevel] = useState<0 | 1 | 2 | 3>(0);
  const [selectedSandboxId, setSelectedSandboxId] = useState<string | undefined>(initialSandboxId);

  const action: ScenarioAction | undefined = useMemo(
    () => allScenarios.find((a) => a.id === actionId),
    [actionId, allScenarios],
  );
  const compareAction: ScenarioAction | undefined = useMemo(
    () => allScenarios.find((a) => a.id === compareActionId),
    [compareActionId, allScenarios],
  );

  const overlayManifest: AtlasManifest | undefined = useMemo(() => {
    if (!selectedSandboxId) return undefined;
    const sandbox = sandboxes.find((s) => s.id === selectedSandboxId);
    if (!sandbox || !sandbox.ops || sandbox.ops.length === 0) return undefined;

    try {
      return applyOps(manifest, sandbox.ops as SandboxOp[]);
    } catch {
      // If overlay application fails, fall back to no overlay
      return undefined;
    }
  }, [selectedSandboxId, sandboxes, manifest]);

  // v1 trace (backward-compat, used for compare stats and scrubber)
  const trace: SimulationTrace | null = useMemo(
    () => (action ? simulate(manifest, action) : null),
    [manifest, action],
  );
  const compareTrace: SimulationTrace | null = useMemo(
    () => (compareAction ? simulate(manifest, compareAction) : null),
    [manifest, compareAction],
  );

  // v2 enriched trace
  const enrichedTrace: EnrichedSimulationTrace | null = useMemo(
    () => (action ? simulateV2(action, manifest, autonomyLevel, undefined, overlayManifest) : null),
    [action, manifest, autonomyLevel, overlayManifest],
  );
  const enrichedCompareTrace: EnrichedSimulationTrace | null = useMemo(
    () => (compareAction ? simulateV2(compareAction, manifest, autonomyLevel) : null),
    [compareAction, manifest, autonomyLevel],
  );

  // Coverage tracker — accumulates across all canonical scenario runs.
  const coverageRef = useRef(new CoverageTracker(manifest, overlayManifest));
  const [coverage, setCoverage] = useState(() => coverageRef.current.snapshot());

  useEffect(() => {
    if (!manifest) return;
    coverageRef.current = new CoverageTracker(manifest, overlayManifest);
    for (const scenario of allScenarios) {
      const t = simulateV2(scenario, manifest, 0, undefined, overlayManifest);
      coverageRef.current.accumulate(t);
    }
    setCoverage(coverageRef.current.snapshot());
  }, [manifest, allScenarios, overlayManifest]);

  // Reset selection when action changes.
  useEffect(() => {
    setSelectedStep(0);
    setPlaying(false);
  }, [actionId]);

  // Auto-play.
  useEffect(() => {
    if (!playing || !trace) return;
    const t = window.setInterval(() => {
      setSelectedStep((s) => {
        if (s >= trace.steps.length - 1) {
          setPlaying(false);
          return s;
        }
        return s + 1;
      });
    }, PLAY_MS);
    return () => window.clearInterval(t);
  }, [playing, trace]);

  const step = trace?.steps[selectedStep];

  return (
    <div className="simulation-view">
      <header className="simulation-head">
        <h2>Simulation Mode</h2>
        <div className="simulation-pickers">
          <label className="simulation-picker">
            Action
            <select
              aria-label="Action"
              value={actionId}
              onChange={(e) => setActionId(e.target.value)}
            >
              <option value="">—</option>
              {allScenarios.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </label>

          {trace && (
            <label className="simulation-picker">
              Compare with
              <select
                aria-label="Compare action"
                value={compareActionId}
                onChange={(e) => setCompareActionId(e.target.value)}
              >
                <option value="">—</option>
                {allScenarios.filter((a) => a.id !== actionId).map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </label>
          )}

          <label className="simulation-picker">
            Autonomy Level
            <select
              aria-label="Autonomy level"
              value={autonomyLevel}
              onChange={(e) => setAutonomyLevel(Number(e.target.value) as 0 | 1 | 2 | 3)}
            >
              <option value={0}>L0 — User-initiated</option>
              <option value={1}>L1 — Assisted</option>
              <option value={2}>L2 — Supervised</option>
              <option value={3}>L3 — Agentic</option>
            </select>
          </label>

          <label className="simulation-picker">
            Sandbox overlay
            <select
              aria-label="Sandbox overlay"
              value={selectedSandboxId ?? ''}
              onChange={(e) => setSelectedSandboxId(e.target.value || undefined)}
            >
              <option value="">(none)</option>
              {sandboxes.map((sb) => (
                <option key={sb.id} value={sb.id}>
                  {sb.name}
                </option>
              ))}
            </select>
          </label>

          {onNavigate && (
            <button
              type="button"
              className="btn btn-ghost simulation-builder-btn"
              onClick={() => onNavigate('scenario-builder')}
            >
              <Icon name="add" size="sm" /> Build Scenario
            </button>
          )}
        </div>

        {focus && !initialAction && (
          <p className="simulation-no-match-note" role="status">
            No canonical scenario writes to {focus} — showing the default view.
          </p>
        )}
      </header>

      {!trace ? (
        <p className="simulation-empty">
          Pick a canonical user action above to see how it propagates through
          the model — entity writes, fired events, triggered Cloud Functions.
        </p>
      ) : (
        <>
          <TabBar tabs={SIM_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Sequence tab — scrubber + sequence diagram */}
          {activeTab === 'sequence' && (
            <>
              <section className="simulation-scrubber" aria-label="Step scrubber">
                <header className="simulation-scrubber-controls">
                  <span className="simulation-scrubber-label">
                    STEP {selectedStep + 1} / {trace.steps.length}
                  </span>
                  <div className="simulation-scrubber-buttons">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      aria-label="Step back"
                      onClick={() => setSelectedStep((s) => Math.max(0, s - 1))}
                      disabled={selectedStep <= 0}
                    >
                      ⏮
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      aria-label={playing ? 'Pause' : 'Play'}
                      onClick={() => {
                        if (playing) setPlaying(false);
                        else { setSelectedStep(0); setPlaying(true); }
                      }}
                    >
                      {playing ? '⏸ Pause' : '⏯ Play'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      aria-label="Step forward"
                      onClick={() => setSelectedStep((s) => Math.min(trace.steps.length - 1, s + 1))}
                      disabled={selectedStep >= trace.steps.length - 1}
                    >
                      ⏭
                    </button>
                  </div>
                </header>

                <div className="simulation-track">
                  {trace.steps.map((s, i) => {
                    const isSelected = i === selectedStep;
                    const enrichedStep = enrichedTrace?.steps[i];
                    const lat = enrichedStep?.latency?.p95;
                    const latClass = lat !== undefined
                      ? lat > 500 ? 'is-high' : lat > 100 ? 'is-med' : 'is-low'
                      : '';
                    const domainColor = stepDomainColor(manifest, s);
                    const gateResult = enrichedStep?.agentGate;
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`simulation-step${isSelected ? ' is-selected' : ''} ${latClass}${gateResult?.tier === 'BLOCK' ? ' is-blocked' : gateResult?.tier === 'WARN' ? ' is-warned' : ''}`}
                        style={{ borderLeftColor: domainColor }}
                        onClick={() => setSelectedStep(i)}
                        aria-label={`Step ${i + 1}: ${s.actor} ${s.source}`}
                      >
                        <span className="simulation-step-num">{i + 1}</span>
                        <span className="simulation-step-kind mono" style={{ color: domainColor }}>
                          {ACTOR_LABEL[s.actor] ?? s.actor.toUpperCase()}
                        </span>
                        {lat !== undefined && (
                          <span className={`simulation-step-lat mono ${latClass}`}>
                            {lat > 1000 ? `${(lat / 1000).toFixed(1)}s` : `${lat}ms`}
                          </span>
                        )}
                        {gateResult && gateResult.tier !== 'ALLOW' && (
                          <span className={`simulation-step-gate mono gate-${gateResult.tier.toLowerCase()}`}>
                            {gateResult.tier}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {step && enrichedTrace && (
                  <StepCard
                    manifest={manifest}
                    step={step}
                    enrichedStep={enrichedTrace.steps[selectedStep]}
                  />
                )}
              </section>

              {!compareAction && enrichedTrace && (
                <SequenceDiagram trace={enrichedTrace} />
              )}

              {compareTrace && compareAction && enrichedTrace && enrichedCompareTrace && (
                <section className="simulation-compare" aria-label="Compare traces">
                  <h3>↔ {action!.label} vs {compareAction.label}</h3>
                  <div className="simulation-compare-stats">
                    <div className="simulation-compare-stat">
                      <div className="simulation-compare-stat-label">{action!.label}</div>
                      <div className="simulation-compare-stat-num">{trace.steps.length} steps</div>
                    </div>
                    <div className="simulation-compare-stat">
                      <div className="simulation-compare-stat-label">{compareAction.label}</div>
                      <div className="simulation-compare-stat-num">{compareTrace.steps.length} steps</div>
                    </div>
                    <div className="simulation-compare-stat">
                      <div className="simulation-compare-stat-label">Δ entities written</div>
                      <div className="simulation-compare-stat-num mono">
                        {Math.abs(uniqueWrites(trace).size - uniqueWrites(compareTrace).size)}
                      </div>
                    </div>
                    <div className="simulation-compare-stat">
                      <div className="simulation-compare-stat-label">Δ total p95 latency</div>
                      <div className="simulation-compare-stat-num mono">
                        {Math.abs(enrichedTrace.totalLatencyMs - enrichedCompareTrace.totalLatencyMs)} ms
                      </div>
                    </div>
                  </div>
                  <div className="simulation-compare-pair">
                    <SequenceDiagram trace={enrichedTrace} />
                    <SequenceDiagram trace={enrichedCompareTrace} />
                  </div>
                </section>
              )}
            </>
          )}

          {/* Waterfall tab */}
          {activeTab === 'waterfall' && enrichedTrace && (
            <WaterfallTimeline trace={enrichedTrace} />
          )}

          {/* Blast Radius tab */}
          {activeTab === 'blast' && enrichedTrace && (
            <BlastRadiusV2 manifest={manifest} trace={enrichedTrace} />
          )}

          {/* Coverage tab */}
          {activeTab === 'coverage' && (
            <CoverageReport
              manifest={manifest}
              coverage={coverage}
              scenarios={allScenarios}
              onSuggest={onNavigate ? (entityId) => onNavigate('scenario-builder', entityId) : undefined}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── StepCard ─────────────────────────────────────────────────────────────────

interface StepCardProps {
  manifest: AtlasManifest;
  step: SimulationTrace['steps'][number];
  enrichedStep?: EnrichedSimulationTrace['steps'][number];
}

function StepCard({ manifest, step, enrichedStep }: StepCardProps): React.JSX.Element {
  const lat = enrichedStep?.latency;
  const gate = enrichedStep?.agentGate;
  return (
    <div className="simulation-step-card">
      <span className="simulation-step-card-label mono">{step.actor.toUpperCase()}</span>
      <p className="simulation-step-card-body">
        <strong>{step.source}</strong>
        {step.writes.length > 0 && (
          <>
            {' '}writes to{' '}
            {step.writes.map((w, i) => (
              <span key={w}>
                {i > 0 && ', '}
                <EntityChip entityId={w} manifest={manifest} />
              </span>
            ))}
          </>
        )}
        {step.eventsFired.length > 0 && (
          <>
            {' '}· fires{' '}
            {step.eventsFired.map((ev) => (
              <span key={ev} className="chip info">{ev}</span>
            ))}
          </>
        )}
      </p>

      {lat ? (
        <p className="simulation-step-card-warn mono">
          {lat.p95 > 500 ? (
            <><Icon name="warning" size="sm" /> p95 = {lat.p95} ms · investigate write batch</>
          ) : lat.p95 > 100 ? (
            <>↑ p95 = {lat.p95} ms · moderately above median</>
          ) : (
            <>✓ p95 = {lat.p95} ms</>
          )}
        </p>
      ) : (
        <p className="simulation-step-card-warn mono" style={{ color: 'var(--text3)' }}>
          ⓘ Latency telemetry not wired — step counts only.
        </p>
      )}

      {gate && gate.tier !== 'ALLOW' && (
        <p
          className={`simulation-step-card-gate mono gate-${gate.tier.toLowerCase()}`}
          style={{ color: gate.tier === 'BLOCK' ? '#ef4444' : '#f59e0b' }}
        >
          {gate.tier}: {gate.reason}
          {gate.principleViolations.length > 0 && (
            <> · principles: {gate.principleViolations.join(', ')}</>
          )}
        </p>
      )}
    </div>
  );
}
